import React, { useState, useEffect, useRef } from "react";
import {
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdAccessTime,
  MdPerson,
  MdCalendarToday,
  MdPhone,
  MdFilterList,
} from "react-icons/md";
import { FaWhatsapp } from "react-icons/fa";
import CONFIG from "../config";

const AppointmentCalendar = ({
  mode = "view",
  existingAppointments = [],
  onSaveAppointment,
  fetchAppointment,
  onAppointmentClick,
  doctorInfo = {},
  patientData = {},
  loading = false,
  userRole = 3,
  clinicId = null,
  isStandaloneCalendar = false,
  defaultView = "month",
  hideHeader = false,
  theme = {},
  isDarkMode = false
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState(defaultView);
  const [appointments, setAppointments] = useState([]);
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [loadingDoctors, setLoadingDoctors] = useState(false);
  const [selectedDoctorFilter, setSelectedDoctorFilter] = useState("all");
  const [showDoctorFilter, setShowDoctorFilter] = useState(false);
  const [formData, setFormData] = useState({
    NEXT_VISIT_DATE: "",
    NEXT_VISIT_TIME: "",
    NEXT_VISIT_DAY: "",
    DOCTOR_ID: "",
    PATIENT_NAME: "",
    PATIENT_PHONE: "",
    duration: 15,
  });

  const lastFetchedDate = useRef(null);
  const fetchAppointmentRef = useRef(fetchAppointment);

  // Doctor color palette - vibrant and distinct
  const DOCTOR_COLORS = [
    { bg: "#fef3c7", border: "#f59e0b", text: "#92400e", hover: "#fde68a" }, // Amber
    { bg: "#dbeafe", border: "#2563eb", text: "#1e40af", hover: "#bfdbfe" }, // Blue
    { bg: "#d1fae5", border: "#10b981", text: "#065f46", hover: "#a7f3d0" }, // Green
    { bg: "#fce7f3", border: "#ec4899", text: "#831843", hover: "#fbcfe8" }, // Pink
    { bg: "#e0e7ff", border: "#6366f1", text: "#3730a3", hover: "#c7d2fe" }, // Indigo
    { bg: "#fed7aa", border: "#ea580c", text: "#7c2d12", hover: "#fdba74" }, // Orange
    { bg: "#e9d5ff", border: "#a855f7", text: "#581c87", hover: "#d8b4fe" }, // Purple
    { bg: "#ccfbf1", border: "#14b8a6", text: "#134e4a", hover: "#99f6e4" }, // Teal
    { bg: "#fecaca", border: "#ef4444", text: "#7f1d1d", hover: "#fca5a5" }, // Red
    { bg: "#fef08a", border: "#eab308", text: "#713f12", hover: "#fde047" }, // Yellow
  ];

  useEffect(() => {
    fetchAppointmentRef.current = fetchAppointment;
  }, [fetchAppointment]);

  // Fetch doctors for standalone calendar or role 3
  useEffect(() => {
    if ((isStandaloneCalendar || userRole === 3) && clinicId && mode === "edit") {
      fetchClinicDoctors();
    }
  }, []);

  const fetchClinicDoctors = async () => {
    setLoadingDoctors(true);
    try {
      const response = await fetch(
        `${CONFIG.BASE_URL}/doctors/clinic/${clinicId}/doctors`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
            doctorId: doctorInfo.MANDALI_ID,
            clinicId: doctorInfo.CLINIC_ID,
            roleId: doctorInfo.MANDALI_ROLE,
            'Content-Type': 'application/json',
          }
        }
      );

      const data = await response.json();
      if (data.success) {
        setDoctors(data.doctors);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoadingDoctors(false);
    }
  };

  useEffect(() => {
    if (existingAppointments && existingAppointments.length > 0) {
      const formatted = existingAppointments.map((apt, index) => ({
        ...apt,
        id: apt.id || `${apt.PATIENT_ID}-${apt.CASE_ID}-${apt.VISIT_ID}-${index}-${Date.now()}`,
        date: new Date(apt.NEXT_VISIT_DATE || apt.date),
        time: apt.NEXT_VISIT_TIME || apt.time,
        patientName: apt.PATIENT_NAME || apt.patientName || "Patient",
        category: apt.category || "doctor-other-clinic",
      }));
      setAppointments(formatted);
    } else {
      setAppointments([]);
    }
  }, [existingAppointments]);

  useEffect(() => {
    const dateKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;

    if (fetchAppointmentRef.current && lastFetchedDate.current !== dateKey) {
      console.log('Fetching appointments for:', currentDate.toDateString());
      lastFetchedDate.current = dateKey;
      fetchAppointmentRef.current(currentDate);
    }
  }, [currentDate]);

  // Get doctor color by ID
  const getDoctorColor = (doctorId) => {
    if (!doctorId) return DOCTOR_COLORS[1]; // Default blue
    const index = parseInt(doctorId) % DOCTOR_COLORS.length;
    return DOCTOR_COLORS[index];
  };

  // Get doctor initials
  const getDoctorInitials = (doctorName) => {
    if (!doctorName) return "DR";
    const names = doctorName.trim().split(" ");
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  // Get doctor name by ID
  const getDoctorName = (doctorId) => {
    const doctor = doctors.find(d => String(d.MANDALI_ID) === String(doctorId));
    return doctor?.MANDALI_FULL_NAME || "Unknown Doctor";
  };

  const getAppointmentColor = (appointment) => {
    // Use doctor-specific colors
    if (appointment.DOCTOR_ID) {
      return getDoctorColor(appointment.DOCTOR_ID);
    }

    // Fallback to category colors
    const colors = {
      "current-patient": { bg: "#fef3c7", border: "#f59e0b", text: "#92400e", hover: "#fde68a" },
      "doctor-same-clinic": { bg: "#dbeafe", border: "#2563eb", text: "#1e40af", hover: "#bfdbfe" },
      "doctor-other-clinic": { bg: "#e5e7eb", border: "#6b7280", text: "#374151", hover: "#d1d5db" },
    };
    return colors[appointment.category] || colors["doctor-same-clinic"];
  };

  // Filter appointments by selected doctor
  const getFilteredAppointments = () => {
    if (selectedDoctorFilter === "all") return appointments;
    return appointments.filter(apt => String(apt.DOCTOR_ID) === String(selectedDoctorFilter));
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return {
      daysInMonth: new Date(year, month + 1, 0).getDate(),
      startingDayOfWeek: new Date(year, month, 1).getDay(),
    };
  };

  const formatDate = (date) => date.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const formatTime = (hour, minute = 0) => {
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };

  const checkOverlap = (newDate, newTime, duration = 15, doctorId) => {
    const [hours, minutes] = newTime.split(":").map(Number);
    const newStart = new Date(newDate);
    newStart.setHours(hours, minutes, 0);
    const newEnd = new Date(newStart.getTime() + duration * 60000);

    return appointments.some((apt) => {
      if (!apt.date || !apt.time) return false;
      if (String(apt.DOCTOR_ID) !== String(doctorId)) return false;

      const aptDate = new Date(apt.date);
      if (aptDate.toDateString() !== newDate.toDateString()) return false;

      const [aptHours, aptMinutes] = apt.time.split(":").map(Number);
      const aptStart = new Date(aptDate);
      aptStart.setHours(aptHours, aptMinutes, 0);
      const aptEnd = new Date(aptStart.getTime() + (apt.duration || 15) * 60000);

      return newStart < aptEnd && newEnd > aptStart;
    });
  };

  const getAppointmentsForDate = (date) => {
    const filtered = getFilteredAppointments();
    return filtered.filter((apt) => new Date(apt.date).toDateString() === date.toDateString());
  };

  const getAppointmentsForTimeSlot = (date, hour) => {
    const filtered = getFilteredAppointments();
    return filtered.filter((apt) => {
      const aptDate = new Date(apt.date);
      if (aptDate.toDateString() !== date.toDateString()) return false;
      const [aptHour] = apt.time.split(":").map(Number);
      return aptHour === hour;
    });
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const navigateWeek = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + direction * 7);
      return newDate;
    });
  };

  const navigateDay = (direction) => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setDate(prev.getDate() + direction);
      return newDate;
    });
  };

  const handleNavigation = (direction) => {
    if (view === "month") navigateMonth(direction);
    else if (view === "week") navigateWeek(direction);
    else navigateDay(direction);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleMonthYearChange = (month, year) => {
    const newDate = new Date(currentDate);
    if (month !== undefined) newDate.setMonth(month);
    if (year !== undefined) newDate.setFullYear(year);
    setCurrentDate(newDate);
    setShowMonthYearPicker(false);
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^\d{10}$/;
    return phoneRegex.test(phone);
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      setFormData({ ...formData, PATIENT_PHONE: value });
    }
  };

  const renderDoctorFilter = () => {
    if (doctors.length === 0) return null;

    return (
      <div className="doctor-filter-container">
        <button
          className="filter-toggle-btn"
          onClick={() => setShowDoctorFilter(!showDoctorFilter)}
        >
          <MdFilterList size={18} />
          <span>Filter by Doctor</span>
          {selectedDoctorFilter !== "all" && (
            <span className="filter-badge">{doctors.find(d => String(d.MANDALI_ID) === selectedDoctorFilter)?.MANDALI_FULL_NAME || "1"}</span>
          )}
        </button>

        {showDoctorFilter && (
          <div className="doctor-filter-dropdown">
            <div
              className={`filter-option ${selectedDoctorFilter === "all" ? "active" : ""}`}
              onClick={() => {
                setSelectedDoctorFilter("all");
                setShowDoctorFilter(false);
              }}
            >
              <div className="filter-color all-doctors"></div>
              <span>All Doctors</span>
            </div>
            {doctors.map((doctor) => {
              const colors = getDoctorColor(doctor.MANDALI_ID);
              return (
                <div
                  key={doctor.MANDALI_ID}
                  className={`filter-option ${selectedDoctorFilter === String(doctor.MANDALI_ID) ? "active" : ""}`}
                  onClick={() => {
                    setSelectedDoctorFilter(String(doctor.MANDALI_ID));
                    setShowDoctorFilter(false);
                  }}
                >
                  <div
                    className="filter-color"
                    style={{
                      background: colors.bg,
                      borderColor: colors.border
                    }}
                  ></div>
                  <span>{doctor.MANDALI_FULL_NAME}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const renderDoctorLegend = () => {
    if (doctors.length === 0) return null;

    return (
      <div className="doctor-legend-container">
        <div className="legend-title">Doctors:</div>
        <div className="legend-items">
          {doctors.map((doctor) => {
            const colors = getDoctorColor(doctor.MANDALI_ID);
            const initials = getDoctorInitials(doctor.MANDALI_FULL_NAME);
            return (
              <div key={doctor.MANDALI_ID} className="legend-item doctor-legend">
                <div
                  className="doctor-legend-badge"
                  style={{
                    background: colors.bg,
                    borderColor: colors.border,
                    color: colors.text
                  }}
                >
                  {initials}
                </div>
                <span className="doctor-legend-name">{doctor.MANDALI_FULL_NAME}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderMonthYearPicker = () => {
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];
    const years = Array.from({ length: 21 }, (_, i) => currentYear - 10 + i);

    return (
      <div className="month-year-picker-overlay" onClick={() => setShowMonthYearPicker(false)}>
        <div className="month-year-picker" onClick={(e) => e.stopPropagation()}>
          <div className="picker-header">
            <h3>Select Month & Year</h3>
            <button className="close-btn" onClick={() => setShowMonthYearPicker(false)}>
              <MdClose size={20} />
            </button>
          </div>

          <div className="picker-content">
            <div className="picker-section">
              <label className="picker-label">Month</label>
              <div className="month-grid">
                {months.map((month, index) => (
                  <button
                    key={month}
                    className={`month-btn ${index === currentMonth ? 'active' : ''}`}
                    onClick={() => handleMonthYearChange(index, undefined)}
                  >
                    {month.substring(0, 3)}
                  </button>
                ))}
              </div>
            </div>

            <div className="picker-section">
              <label className="picker-label">Year</label>
              <div className="year-grid">
                {years.map((year) => (
                  <button
                    key={year}
                    className={`year-btn ${year === currentYear ? 'active' : ''}`}
                    onClick={() => handleMonthYearChange(undefined, year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleSlotClick = (date, time = null) => {
    if (mode === "view") {
      const dayAppointments = getAppointmentsForDate(date);
      if (dayAppointments.length > 0) setSelectedAppointment(dayAppointments[0]);
      return;
    }

    const now = new Date();
    const selectedDate = new Date(date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(selectedDate);
    checkDate.setHours(0, 0, 0, 0);

    if (checkDate < today) {
      alert("Cannot schedule appointments for past dates. Please select today or a future date.");
      return;
    }

    if (time && checkDate.getTime() === today.getTime()) {
      const [hours, minutes] = time.split(":").map(Number);
      const selectedDateTime = new Date(selectedDate);
      selectedDateTime.setHours(hours, minutes, 0, 0);

      if (selectedDateTime <= now) {
        alert("Cannot schedule appointments for past times. Please select a future time.");
        return;
      }
    }

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const day = String(selectedDate.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    const dayName = selectedDate.toLocaleDateString("en-US", { weekday: "long" });

    setSelectedSlot({ date: selectedDate, time });
    setFormData({
      NEXT_VISIT_DATE: dateStr,
      NEXT_VISIT_TIME: time || "09:00",
      NEXT_VISIT_DAY: dayName,
      DOCTOR_ID: (isStandaloneCalendar || userRole === 3) ? "" : doctorInfo?.MANDALI_ID || "",
      PATIENT_NAME: "",
      PATIENT_PHONE: "",
      duration: 15,
    });
    setShowAppointmentForm(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();

    const appointmentDate = new Date(formData.NEXT_VISIT_DATE);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    appointmentDate.setHours(0, 0, 0, 0);

    if (appointmentDate < today) {
      alert("Cannot schedule appointments for past dates. Please select today or a future date.");
      return;
    }

    if (isStandaloneCalendar) {
      if (!formData.DOCTOR_ID) {
        alert("Please select a doctor for this appointment.");
        return;
      }
      if (!formData.PATIENT_NAME || formData.PATIENT_NAME.trim() === "") {
        alert("Please enter patient name.");
        return;
      }
      if (!formData.PATIENT_PHONE || !validatePhoneNumber(formData.PATIENT_PHONE)) {
        alert("Please enter a valid 10-digit phone number.");
        return;
      }
    } else if (userRole === 3 && !formData.DOCTOR_ID) {
      alert("Please select a doctor for this appointment.");
      return;
    }

    if (checkOverlap(
      appointmentDate,
      formData.NEXT_VISIT_TIME,
      formData.duration,
      formData.DOCTOR_ID || doctorInfo?.MANDALI_ID
    )) {
      alert("This time slot overlaps with the same doctor's appointment. Please choose a different time.");
      return;
    }

    if (onSaveAppointment) {
      const appointmentData = {
        NEXT_VISIT_DATE: formData.NEXT_VISIT_DATE,
        NEXT_VISIT_TIME: formData.NEXT_VISIT_TIME,
        NEXT_VISIT_DAY: formData.NEXT_VISIT_DAY,
        DOCTOR_ID: formData.DOCTOR_ID,
        duration: formData.duration,
      };

      if (isStandaloneCalendar) {
        appointmentData.PATIENT_NAME = formData.PATIENT_NAME;
        appointmentData.PATIENT_PHONE = formData.PATIENT_PHONE;
      }

      onSaveAppointment(appointmentData);
    }

    setShowAppointmentForm(false);
    setFormData({
      NEXT_VISIT_DATE: "",
      NEXT_VISIT_TIME: "",
      NEXT_VISIT_DAY: "",
      DOCTOR_ID: "",
      PATIENT_NAME: "",
      PATIENT_PHONE: "",
      duration: 15
    });
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];
    const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayAppointments = getAppointmentsForDate(date);
      const isToday = date.toDateString() === new Date().toDateString();
      const isPastDate = date < new Date().setHours(0, 0, 0, 0);

      days.push(
        <div
          key={day}
          className={`calendar-day ${isToday ? "today" : ""} ${mode === "edit" && !isPastDate ? "clickable" : ""} ${isPastDate ? "past-date" : ""}`}
          onClick={() => handleSlotClick(date)}
        >
          <div className="day-number">{day}</div>
          <div className="appointments-container">
            {dayAppointments.slice(0, 3).map((apt) => {
              const colors = getAppointmentColor(apt);
              const doctorInitials = apt.DOCTOR_NAME ? getDoctorInitials(apt.DOCTOR_NAME) : getDoctorInitials(getDoctorName(apt.DOCTOR_ID));
              return (
                <div
                  key={apt.id}
                  className="appointment-badge"
                  style={{ background: colors.bg, borderLeftColor: colors.border, color: colors.text }}
                  onClick={(e) => { e.stopPropagation(); setSelectedAppointment(apt); }}
                >
                  <div className="apt-badge-doctor">{doctorInitials}</div>
                  <MdAccessTime size={10} />
                  <span>{apt.time}</span>
                </div>
              );
            })}
            {dayAppointments.length > 3 && (
              <div className="more-appointments">+{dayAppointments.length - 3} more</div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="month-view">
        <div className="weekday-header">
          {weekDays.map((day) => <div key={day} className="weekday-label">{day}</div>)}
        </div>
        <div className="calendar-grid">{days}</div>
      </div>
    );
  };

  const renderWeekView = () => {
    const startOfWeek = new Date(currentDate);
    startOfWeek.setDate(currentDate.getDate() - currentDate.getDay());

    const weekDays = Array.from({ length: 7 }, (_, i) => {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      return day;
    });

    const hours = Array.from({ length: 12 }, (_, i) => i + 8);

    return (
      <div className="week-view">
        <div className="week-header">
          <div className="time-column-header">Time</div>
          {weekDays.map((day) => (
            <div key={day.toISOString()} className="day-column-header">
              <div className="day-name">{day.toLocaleDateString("en-US", { weekday: "short" })}</div>
              <div className="day-date">{day.getDate()}</div>
            </div>
          ))}
        </div>
        <div className="week-grid">
          {hours.map((hour) => (
            <div key={hour} className="time-row">
              <div className="time-label">{formatTime(hour)}</div>
              {weekDays.map((day) => {
                const slotAppointments = getAppointmentsForTimeSlot(day, hour);
                const isPastDate = day < new Date().setHours(0, 0, 0, 0);
                return (
                  <div
                    key={`${day.toISOString()}-${hour}`}
                    className={`time-slot ${mode === "edit" && !isPastDate ? "clickable" : ""} ${isPastDate ? "past-date" : ""}`}
                    onClick={() => handleSlotClick(day, `${hour.toString().padStart(2, "0")}:00`)}
                  >
                    {slotAppointments.map((apt) => {
                      const colors = getAppointmentColor(apt);
                      const doctorInitials = apt.DOCTOR_NAME ? getDoctorInitials(apt.DOCTOR_NAME) : getDoctorInitials(getDoctorName(apt.DOCTOR_ID));
                      return (
                        <div
                          key={apt.id}
                          className="appointment-block"
                          style={{ background: colors.bg, borderLeftColor: colors.border, color: colors.text }}
                          onClick={(e) => { e.stopPropagation(); setSelectedAppointment(apt); }}
                        >
                          <div className="apt-block-header">
                            <div className="apt-block-doctor">{doctorInitials}</div>
                            <div className="apt-time">{apt.time}</div>
                          </div>
                          <div className="apt-patient">{apt.patientName}</div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderDayView = () => {
    // Expanded hours: 6 AM to 11 PM (18 hours)
    const hours = Array.from({ length: 18 }, (_, i) => i + 6);

    return (
      <div className="day-view">
        <div className="day-view-header">
          <div className="current-day-label">
            {currentDate.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>
        <div className="day-schedule">
          {hours.map((hour) => {
            const slotAppointments = getAppointmentsForTimeSlot(currentDate, hour);
            const isPastDate = currentDate < new Date().setHours(0, 0, 0, 0);
            return (
              <div key={hour} className="hour-slot">
                <div className="hour-label">{formatTime(hour)}</div>
                <div
                  className={`hour-content ${mode === "edit" && !isPastDate ? "clickable" : ""} ${isPastDate ? "past-date" : ""}`}
                  onClick={() => handleSlotClick(currentDate, `${hour.toString().padStart(2, "0")}:00`)}
                >
                  {slotAppointments.map((apt) => {
                    const colors = getAppointmentColor(apt);
                    const doctorName = apt.DOCTOR_NAME || getDoctorName(apt.DOCTOR_ID);
                    const doctorInitials = getDoctorInitials(doctorName);
                    return (
                      <div
                        key={apt.id}
                        className="appointment-card"
                        style={{ background: colors.bg, borderLeftColor: colors.border, color: colors.text }}
                        onClick={(e) => { e.stopPropagation(); setSelectedAppointment(apt); }}
                      >
                        <div className="apt-card-doctor-badge" style={{ background: colors.border, color: 'white' }}>
                          {doctorInitials}
                        </div>
                        <div className="apt-card-content">
                          <div className="apt-header">
                            <MdAccessTime size={14} />
                            <span>{apt.time}</span>
                          </div>
                          <div className="apt-doctor-name">{doctorName}</div>
                          <div className="apt-details">
                            <MdPerson size={14} />
                            <span>{apt.patientName || "Patient Appointment"}</span>
                          </div>
                          {apt.duration && <div className="apt-duration">{apt.duration} minutes</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const getCategoryLabel = (category) => {
    const labels = {
      "current-patient": "Current Patient",
      "doctor-same-clinic": "Same Clinic",
      "doctor-other-clinic": "Other Clinic",
    };
    return labels[category] || "Appointment";
  };

  return (
    <div className="appointment-calendar">
      <style>{`
        .appointment-calendar {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          background: ${isDarkMode ? '#121212' : '#FFFFFF'};
          color: ${isDarkMode ? theme.text : '#1F2937'};
          border-radius: 12px;
          padding: ${hideHeader ? '16px' : '24px'};
          max-width: ${hideHeader ? 'none' : '1400px'};
          margin: ${hideHeader ? '0' : '0 auto'};
          display: flex;
          flex-direction: column;
          height: 100%;
          border: ${isDarkMode ? '1px solid #333333' : '1px solid #E5E7EB'};
          box-shadow: ${isDarkMode ? 'none' : '0 4px 6px -1px rgba(0, 0, 0, 0.1)'};
        }
        
        .day-view {
          display: flex;
          flex-direction: column;
          height: 100%;
          flex: 1;
        }

        .day-schedule {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: ${isDarkMode ? 'rgba(255,255,255,0.2)' : '#d1d5db'} transparent;
        }

        /* Dark Mode Adjustments */
        .hour-label {
          background: ${isDarkMode ? theme.card : 'white'};
          color: ${isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'};
        }
        .hour-content {
          background: ${isDarkMode ? theme.inputBg : 'white'};
        }
        .hour-slot {
          background: ${isDarkMode ? theme.border : '#e5e7eb'};
        }
        .day-view-header {
          background: ${isDarkMode ? '#1E1E1E' : '#F3F4F6'};
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
          padding: 16px;
        }
        .current-day-label {
          color: ${isDarkMode ? theme.text : '#111827'};
          font-weight: 700;
        }
        .weekday-label {
          background: ${isDarkMode ? '#1E1E1E' : '#F9FAFB'};
          color: ${isDarkMode ? '#A1A1AA' : '#4B5563'};
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.75rem;
          letter-spacing: 0.05em;
          padding: 12px;
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .calendar-day {
          background: ${isDarkMode ? '#121212' : '#FFFFFF'};
          border-right: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .day-number {
          color: ${isDarkMode ? theme.text : '#1F2937'};
          font-weight: 500;
        }
        .weekday-header {
          background: ${isDarkMode ? '#1E1E1E' : '#F9FAFB'};
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .calendar-grid {
          background: ${isDarkMode ? '#333333' : '#E5E7EB'};
          gap: 1px;
          border: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .calendar-grid {
          grid-template-columns: repeat(7, 1fr);
        }
        .calendar-day.empty {
          background: ${isDarkMode ? '#121212' : '#F9FAFB'};
        }
        .calendar-day.past-date {
          background: ${isDarkMode ? '#0A0A0A' : '#F9FAFB'};
          color: ${isDarkMode ? '#525252' : '#9CA3AF'};
        }
        .calendar-day.today {
          background: ${isDarkMode ? 'rgba(59, 130, 246, 0.1)' : '#EFF6FF'};
        }
        .calendar-day.today .day-number {
          background: #3B82F6;
          color: #FFFFFF;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .time-label {
          background: ${isDarkMode ? '#1E1E1E' : '#FFFFFF'};
          color: ${isDarkMode ? 'rgba(255,255,255,0.7)' : '#6B7280'};
          border-right: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .time-column-header, .day-column-header {
          background: ${isDarkMode ? '#1E1E1E' : '#F9FAFB'};
          color: ${isDarkMode ? theme.text : '#1F2937'};
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .time-slot {
          background: ${isDarkMode ? '#121212' : '#FFFFFF'};
          border-bottom: 1px solid ${isDarkMode ? '#262626' : '#F3F4F6'};
          border-right: 1px solid ${isDarkMode ? '#262626' : '#F3F4F6'};
        }
        .time-row {
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .week-header {
          background: ${isDarkMode ? '#1E1E1E' : '#F9FAFB'};
          border-bottom: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
        }
        .day-name {
          color: ${isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'};
        }
        .day-date {
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .doctor-legend-container {
          background: ${isDarkMode ? '#1E1E1E' : '#F3F4F6'};
          border: 1px solid ${isDarkMode ? '#333333' : '#E5E7EB'};
          border-radius: 8px;
          padding: 12px 16px;
        }
        .legend-title {
          color: ${isDarkMode ? theme.text : '#111827'};
          font-weight: 600;
        }
        .doctor-legend-name {
          color: ${isDarkMode ? 'rgba(255,255,255,0.8)' : '#374151'};
        }
        .legend-item.doctor-legend {
          background: ${isDarkMode ? '#262626' : '#FFFFFF'};
          border: 1px solid ${isDarkMode ? '#404040' : '#E5E7EB'};
          border-radius: 6px;
        }
        .modal {
          background: ${isDarkMode ? theme.card : 'white'};
          border: ${isDarkMode ? `1px solid ${theme.border}` : 'none'};
        }
        .modal-title, .form-label, .detail-value {
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .form-control {
          background: ${isDarkMode ? theme.inputBg : 'white'};
          border-color: ${isDarkMode ? theme.border : '#d1d5db'};
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .detail-row {
          background: ${isDarkMode ? theme.inputBg : '#f9fafb'};
        }
        .modal-header, .form-actions, .picker-header {
          border-color: ${isDarkMode ? theme.border : '#e5e7eb'};
        }
        .month-year-picker {
          background: ${isDarkMode ? theme.card : 'white'};
        }
        .month-btn, .year-btn {
          background: ${isDarkMode ? theme.inputBg : 'white'};
          border-color: ${isDarkMode ? theme.border : '#d1d5db'};
          color: ${isDarkMode ? theme.text : '#374151'};
        }
        .picker-header h3, .picker-label {
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .view-btn {
          padding: 8px 16px;
          border: 1px solid transparent;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: ${isDarkMode ? '#A1A1AA' : '#6B7280'};
          transition: all 0.2s;
        }
        .view-btn:hover {
          color: ${isDarkMode ? '#FFFFFF' : '#111827'};
          background: ${isDarkMode ? '#262626' : '#F3F4F6'};
        }
        .view-btn.active {
          background: ${isDarkMode ? '#262626' : '#FFFFFF'};
          color: ${isDarkMode ? '#60A5FA' : '#2563EB'};
          border-color: ${isDarkMode ? '#404040' : '#E5E7EB'};
          box-shadow: 0 1px 2px rgba(0,0,0,0.05);
        }

        /* Doctor Filter Styles */
        .doctor-filter-container {
          position: relative;
        }
        .filter-toggle-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: ${isDarkMode ? theme.card : 'white'};
          border: 1px solid ${isDarkMode ? theme.border : '#d1d5db'};
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: ${isDarkMode ? theme.text : '#374151'};
          transition: all 0.2s;
        }
        .filter-toggle-btn:hover {
          background: ${isDarkMode ? theme.inputBg : '#f3f4f6'};
          border-color: ${isDarkMode ? theme.border : '#9ca3af'};
        }
        .filter-badge {
          background: #2563eb;
          color: white;
          padding: 2px 8px;
          border-radius: 10px;
          font-size: 11px;
          font-weight: 600;
        }
        .doctor-filter-dropdown {
          position: absolute;
          top: 100%;
          right: 0;
          margin-top: 8px;
          background: ${isDarkMode ? theme.card : 'white'};
          border: 1px solid ${isDarkMode ? theme.border : '#d1d5db'};
          border-radius: 8px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          min-width: 250px;
          z-index: 100;
          max-height: 400px;
          overflow-y: auto;
        }
        .filter-option {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          cursor: pointer;
          transition: background 0.2s;
          border-bottom: 1px solid #f3f4f6;
        }
        .filter-option:last-child {
          border-bottom: none;
        }
        .filter-option:hover {
          background: ${isDarkMode ? theme.inputBg : '#f9fafb'};
        }
        .filter-option.active {
          background: ${isDarkMode ? theme.primary + '20' : '#eff6ff'};
        }
        .filter-color {
          width: 24px;
          height: 24px;
          border-radius: 4px;
          border: 2px solid;
          flex-shrink: 0;
        }
        .filter-color.all-doctors {
          background: linear-gradient(135deg, #fef3c7 0%, #dbeafe 25%, #d1fae5 50%, #fce7f3 75%, #e0e7ff 100%);
          border-color: #6b7280;
        }

        /* Doctor Legend Styles */
        .doctor-legend-container {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 12px 16px;
          background: #f9fafb;
          border-radius: 8px;
          margin-bottom: 20px;
          flex-wrap: wrap;
        }
        .legend-title {
          font-weight: 600;
          color: #374151;
          font-size: 14px;
        }
        .legend-items {
          display: flex;
          gap: 20px;
          flex-wrap: wrap;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 13px;
        }
        .legend-item.doctor-legend {
          background: white;
          padding: 6px 12px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }
        .doctor-legend-badge {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 11px;
          flex-shrink: 0;
        }
        .doctor-legend-name {
          font-weight: 500;
          color: #374151;
        }

        .month-view { width: 100%; }
        .weekday-header {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 1px;
          background: #e5e7eb;
          margin-bottom: 1px;
        }
        .weekday-label {
          background: ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.5)'};
          padding: 12px;
          text-align: center;
          font-weight: 600;
          font-size: 14px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'};
          backdrop-filter: blur(5px);
          border-radius: 8px;
          margin-bottom: 4px;
        }
        .calendar-grid {
          display: grid;
          grid-template-columns: repeat(7, 1fr);
          gap: 6px;
          background: transparent;
        }
        .calendar-day {
          background: ${isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.4)'};
          min-height: 100px;
          padding: 8px;
          position: relative;
          border-radius: 12px;
          backdrop-filter: blur(5px);
          border: 1px solid ${isDarkMode ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.2)'};
        }
        .calendar-day.empty { background: #f9fafb; }
        .calendar-day.today { background: #eff6ff; }
        .calendar-day.clickable {
          cursor: pointer;
          transition: background 0.2s;
        }
        .calendar-day.clickable:hover { 
          background: ${isDarkMode ? '#1E1E1E' : '#F9FAFB'}; 
        }
        .calendar-day.past-date {
          background: ${isDarkMode ? '#0A0A0A' : '#FAFAFA'};
          color: ${isDarkMode ? '#525252' : '#9CA3AF'};
          cursor: not-allowed;
          opacity: 1; 
        }
        .calendar-day.past-date .day-number { color: #9ca3af; }
        .day-number {
          font-weight: 600;
          color: #111827;
          margin-bottom: 8px;
        }
        .calendar-day.today .day-number {
          background: #2563eb;
          color: white;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
        }
        .appointments-container {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .appointment-badge {
          border-left: 3px solid;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 11px;
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .appointment-badge:hover {
          opacity: 0.8;
          transform: translateX(2px);
        }
        .apt-badge-doctor {
          background: rgba(0,0,0,0.1);
          padding: 2px 5px;
          border-radius: 3px;
          font-weight: 700;
          font-size: 9px;
        }
        .more-appointments {
          color: #6b7280;
          font-size: 11px;
          padding: 2px 8px;
          font-weight: 500;
        }

        .week-view, .day-view { overflow-x: auto; }
        .week-header {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          gap: 1px;
          background: #e5e7eb;
          margin-bottom: 1px;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .time-column-header, .day-column-header {
          background: ${isDarkMode ? theme.card : 'white'};
          padding: 12px;
          text-align: center;
          font-weight: 600;
          font-size: 14px;
        }
        .day-column-header {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .day-name { color: ${isDarkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'}; }
        .day-date {
          color: ${isDarkMode ? theme.text : '#111827'};
          font-size: 18px;
        }
        .week-grid {
          display: flex;
          flex-direction: column;
        }
        .time-row {
          display: grid;
          grid-template-columns: 80px repeat(7, 1fr);
          gap: 1px;
          background: #e5e7eb;
          margin-bottom: 1px;
        }
        .time-label {
          background: ${isDarkMode ? theme.card : 'white'};
          padding: 12px 8px;
          text-align: right;
          font-size: 12px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'};
          font-weight: 500;
        }
        .time-slot {
          background: ${isDarkMode ? theme.inputBg : 'white'};
          min-height: 60px;
          padding: 4px;
          position: relative;
        }
        .time-slot.clickable {
          cursor: pointer;
          transition: background 0.2s;
        }
        .time-slot.clickable:hover { background: ${isDarkMode ? theme.border : '#f9fafb'}; }
        .time-slot.past-date {
          background: ${isDarkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb'};
          cursor: not-allowed;
        }
        .appointment-block {
          border-left: 3px solid;
          padding: 8px;
          border-radius: 4px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 4px;
        }
        .appointment-block:hover {
          opacity: 0.8;
          transform: translateX(2px);
        }
        .apt-block-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        }
        .apt-block-doctor {
          background: rgba(0,0,0,0.15);
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 700;
          font-size: 10px;
        }
        .apt-time {
          font-size: 12px;
          font-weight: 600;
        }
        .apt-patient { 
          font-size: 11px;
          margin-top: 2px;
        }

        .day-view-header {
          background: ${isDarkMode ? theme.inputBg : '#f9fafb'};
          padding: 16px;
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .current-day-label {
          font-size: 20px;
          font-weight: 600;
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .day-schedule {
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          overflow-y: auto;
          scrollbar-width: thin;
          scrollbar-color: ${isDarkMode ? '#4b5563 transparent' : '#d1d5db transparent'};
        }
        .day-schedule::-webkit-scrollbar {
          width: 6px;
        }
        .day-schedule::-webkit-scrollbar-track {
          background: transparent;
        }
        .day-schedule::-webkit-scrollbar-thumb {
          background-color: ${isDarkMode ? '#4b5563' : '#d1d5db'};
          border-radius: 20px;
        }
        .hour-slot {
          display: grid;
          grid-template-columns: 80px 1fr;
          gap: 1px;
          background: ${isDarkMode ? theme.border : '#e5e7eb'};
          margin-bottom: 1px;
        }
        .hour-label {
          background: ${isDarkMode ? theme.card : 'white'};
          padding: 16px 8px;
          text-align: right;
          font-size: 14px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.7)' : '#6b7280'};
          font-weight: 500;
        }
        .hour-content {
          background: ${isDarkMode ? theme.inputBg : 'white'};
          min-height: 80px;
          padding: 8px;
        }
        .hour-content.clickable {
          cursor: pointer;
          transition: background 0.2s;
        }
        .hour-content.clickable:hover { background: ${isDarkMode ? theme.border : '#f9fafb'}; }
        .hour-content.past-date {
          background: ${isDarkMode ? 'rgba(0,0,0,0.2)' : '#f9fafb'};
          cursor: not-allowed;
        }
        .appointment-card {
          border-left: 4px solid;
          padding: 12px;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          margin-bottom: 8px;
          display: flex;
          gap: 12px;
          position: relative;
        }
        .appointment-card:hover {
          opacity: 0.8;
          transform: translateY(-1px);
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        .apt-card-doctor-badge {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 13px;
          flex-shrink: 0;
        }
        .apt-card-content {
          flex: 1;
        }
        .apt-header, .apt-details {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 6px;
        }
        .apt-header { font-weight: 600; }
        .apt-doctor-name {
          font-weight: 600;
          font-size: 13px;
          margin-bottom: 6px;
        }
        .apt-duration {
          font-size: 12px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'};
          margin-top: 4px;
        }

        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: ${isDarkMode ? theme.card : 'white'};
          border-radius: 12px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
          border: ${isDarkMode ? `1px solid ${theme.border}` : 'none'};
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #e5e7eb;
        }
        .modal-title {
          font-size: 20px;
          font-weight: 600;
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 4px;
          color: ${isDarkMode ? 'rgba(255,255,255,0.6)' : '#6b7280'};
          transition: color 0.2s;
        }
        .close-btn:hover { color: ${isDarkMode ? theme.text : '#111827'}; }
        .form-group { margin-bottom: 20px; }
        .form-label {
          display: block;
          margin-bottom: 6px;
          font-weight: 500;
          color: ${isDarkMode ? theme.text : '#374151'};
          font-size: 14px;
        }
        .form-control {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid ${isDarkMode ? theme.border : '#d1d5db'};
          border-radius: 6px;
          font-size: 14px;
          transition: all 0.2s;
          box-sizing: border-box;
          background: ${isDarkMode ? theme.inputBg : 'white'};
          color: ${isDarkMode ? theme.text : 'inherit'};
        }
        .form-control:focus {
          outline: none;
          border-color: #2563eb;
          box-shadow: 0 0 0 3px rgba(37,99,235,0.1);
        }
        .form-control:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }
        .form-control.error {
          border-color: #ef4444;
        }
        .error-message {
          color: #ef4444;
          font-size: 12px;
          margin-top: 4px;
        }
        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 2px solid #e5e7eb;
        }
        .btn {
          padding: 10px 20px;
          border-radius: 6px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          border: none;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .btn-primary {
          background: #2563eb;
          color: white;
        }
        .btn-primary:hover:not(:disabled) { background: #1d4ed8; }
        .btn-secondary {
          background: #f3f4f6;
          color: #374151;
          border: 1px solid #d1d5db;
        }
        .btn-secondary:hover { background: #e5e7eb; }
        .detail-row {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: ${isDarkMode ? theme.inputBg : '#f9fafb'};
          border-radius: 6px;
          margin-bottom: 12px;
        }
        .detail-label {
          font-weight: 600;
          color: ${isDarkMode ? '#A1A1AA' : '#6B7280'};
          min-width: 100px;
        }
        .detail-value { color: ${isDarkMode ? theme.text : '#111827'}; font-weight: 500; }
        .category-badge {
          display: inline-flex;
          align-items: center;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
          border: 2px solid;
        }
        .category-badge.current-patient {
          background: #fef3c7;
          border-color: #f59e0b;
          color: #92400e;
        }
        .category-badge.doctor-same-clinic {
          background: #dbeafe;
          border-color: #2563eb;
          color: #1e40af;
        }
        .category-badge.doctor-other-clinic {
          background: #e5e7eb;
          border-color: #6b7280;
          color: #374151;
        }

        /* Modal Doctor Info */
        .modal-doctor-header {
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 16px;
          background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
          border-radius: 8px;
          margin-bottom: 20px;
        }
        .modal-doctor-avatar {
          width: 60px;
          height: 60px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 20px;
          border: 3px solid;
          flex-shrink: 0;
        }
        .modal-doctor-info {
          flex: 1;
        }
        .modal-doctor-name {
          font-size: 18px;
          font-weight: 700;
          color: #111827;
          margin-bottom: 4px;
        }
        .modal-doctor-label {
          font-size: 12px;
          color: #6b7280;
          text-transform: uppercase;
          font-weight: 600;
          letter-spacing: 0.5px;
        }

        .month-year-picker-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .month-year-picker {
          background: ${isDarkMode ? theme.card : 'white'};
          border-radius: 12px;
          padding: 24px;
          max-width: 500px;
          width: 90%;
          max-height: 90vh;
          overflow-y: auto;
          box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
          border: ${isDarkMode ? `1px solid ${theme.border}` : 'none'};
        }
        .picker-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 2px solid #e5e7eb;
        }
        .picker-header h3 {
          margin: 0;
          font-size: 20px;
          font-weight: 600;
          color: ${isDarkMode ? theme.text : '#111827'};
        }
        .picker-content {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }
        .picker-section {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .picker-label {
          font-weight: 600;
          color: ${isDarkMode ? theme.text : '#374151'};
          font-size: 14px;
        }
        .month-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 8px;
        }
        .month-btn {
          padding: 12px;
          border: 1px solid ${isDarkMode ? theme.border : '#d1d5db'};
          border-radius: 6px;
          background: ${isDarkMode ? theme.inputBg : 'white'};
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: ${isDarkMode ? theme.text : '#374151'};
          transition: all 0.2s;
        }
        .month-btn:hover {
          background: ${isDarkMode ? theme.border : '#f3f4f6'};
          border-color: ${isDarkMode ? theme.primary : '#9ca3af'};
        }
        .month-btn.active {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
        }
        .year-grid {
          display: grid;
          grid-template-columns: repeat(5, 1fr);
          gap: 8px;
          max-height: 300px;
          overflow-y: auto;
        }
        .year-btn {
          padding: 12px;
          border: 1px solid ${isDarkMode ? theme.border : '#d1d5db'};
          border-radius: 6px;
          background: ${isDarkMode ? theme.inputBg : 'white'};
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
          color: ${isDarkMode ? theme.text : '#374151'};
          transition: all 0.2s;
        }
        .year-btn:hover {
          background: ${isDarkMode ? theme.border : '#f3f4f6'};
          border-color: ${isDarkMode ? theme.primary : '#9ca3af'};
        }
        .year-btn.active {
          background: #2563eb;
          color: white;
          border-color: #2563eb;
        }
        @media (max-width: 768px) {
          .calendar-header {
            flex-direction: column;
            align-items: stretch;
          }
          .header-left {
            flex-direction: column;
            align-items: stretch;
          }
          .doctor-legend-container {
            flex-direction: column;
            align-items: stretch;
          }
          .legend-items { flex-direction: column; }
        }
      `}</style>

      {!hideHeader && (
        <div className="calendar-header">
          <div className="header-left">
            <div className="nav-buttons">
              <button className="nav-btn" onClick={() => handleNavigation(-1)} title="Previous">
                <MdChevronLeft size={20} />
              </button>
              <button className="nav-btn" onClick={goToToday} title="Go to Today">
                Today
              </button>
              <button className="nav-btn" onClick={() => handleNavigation(1)} title="Next">
                <MdChevronRight size={20} />
              </button>
            </div>
            <div className="current-date" onClick={() => setShowMonthYearPicker(true)} title="Click to select month/year">
              {formatDate(currentDate)}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {renderDoctorFilter()}
            <div className="view-switcher">
              <button className={`view-btn ${view === "month" ? "active" : ""}`} onClick={() => setView("month")}>
                Month
              </button>
              <button className={`view-btn ${view === "week" ? "active" : ""}`} onClick={() => setView("week")}>
                Week
              </button>
              <button className={`view-btn ${view === "day" ? "active" : ""}`} onClick={() => setView("day")}>
                Day
              </button>
            </div>
          </div>
        </div>
      )}
      {renderDoctorLegend()}
      {view === "month" && renderMonthView()}
      {view === "week" && renderWeekView()}
      {view === "day" && renderDayView()}

      {showMonthYearPicker && renderMonthYearPicker()}

      {showAppointmentForm && (
        <div className="modal-overlay" onClick={() => setShowAppointmentForm(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Schedule Appointment</h2>
              <button className="close-btn" onClick={() => setShowAppointmentForm(false)}>
                <MdClose size={20} />
              </button>
            </div>
            <form onSubmit={handleFormSubmit}>
              {(isStandaloneCalendar || userRole === 3) && (
                <div className="form-group">
                  <label className="form-label">Select Doctor *</label>
                  <select
                    className="form-control"
                    value={formData.DOCTOR_ID}
                    onChange={(e) => setFormData({ ...formData, DOCTOR_ID: e.target.value })}
                    required
                    disabled={loadingDoctors}
                  >
                    <option value="">
                      {loadingDoctors ? 'Loading doctors...' : 'Choose a doctor'}
                    </option>
                    {doctors.map((doctor) => (
                      <option key={doctor.MANDALI_ID} value={doctor.MANDALI_ID}>
                        {doctor.MANDALI_FULL_NAME}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {isStandaloneCalendar && (
                <>
                  <div className="form-group">
                    <label className="form-label">Patient Name *</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.PATIENT_NAME}
                      onChange={(e) => setFormData({ ...formData, PATIENT_NAME: e.target.value })}
                      placeholder="Enter patient name"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Patient Phone Number *</label>
                    <input
                      type="tel"
                      className={`form-control ${formData.PATIENT_PHONE && !validatePhoneNumber(formData.PATIENT_PHONE) ? 'error' : ''}`}
                      value={formData.PATIENT_PHONE}
                      onChange={handlePhoneChange}
                      placeholder="Enter 10-digit phone number"
                      maxLength="10"
                      required
                    />
                    {formData.PATIENT_PHONE && !validatePhoneNumber(formData.PATIENT_PHONE) && (
                      <div className="error-message">Please enter a valid 10-digit phone number</div>
                    )}
                  </div>
                </>
              )}

              <div className="form-group">
                <label className="form-label">Next Visit Date *</label>
                <input
                  type="date"
                  className="form-control"
                  value={formData.NEXT_VISIT_DATE}
                  onChange={(e) => {
                    const date = new Date(e.target.value);
                    const dayName = date.toLocaleDateString("en-US", { weekday: "long" });
                    setFormData({
                      ...formData,
                      NEXT_VISIT_DATE: e.target.value,
                      NEXT_VISIT_DAY: dayName,
                    });
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Next Visit Time *</label>
                <input
                  type="time"
                  className="form-control"
                  value={formData.NEXT_VISIT_TIME}
                  onChange={(e) => setFormData({ ...formData, NEXT_VISIT_TIME: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Next Visit Day</label>
                <input type="text" className="form-control" value={formData.NEXT_VISIT_DAY} readOnly />
              </div>

              <div className="form-group">
                <label className="form-label">Duration (minutes) *</label>
                <select
                  className="form-control"
                  value={formData.duration}
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAppointmentForm(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={loadingDoctors}>
                  Save Appointment
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {selectedAppointment && (
        <div className="modal-overlay" onClick={() => setSelectedAppointment(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">Appointment Details</h2>
              <button className="close-btn" onClick={() => setSelectedAppointment(null)}>
                <MdClose size={20} />
              </button>
            </div>

            <div className="detail-row">
              <div>
                <div className="detail-label">Category</div>
                <div className="detail-value">
                  <span className={`category-badge ${selectedAppointment.category}`}>
                    {getCategoryLabel(selectedAppointment.category)}
                  </span>
                </div>
              </div>
            </div>

            <div className="detail-row">
              <MdCalendarToday size={18} />
              <div>
                <div className="detail-label">Date</div>
                <div className="detail-value">
                  {new Date(selectedAppointment.date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </div>
              </div>
            </div>

            <div className="detail-row">
              <MdAccessTime size={18} />
              <div>
                <div className="detail-label">Time</div>
                <div className="detail-value">{selectedAppointment.time}</div>
              </div>
            </div>

            {selectedAppointment.duration && (
              <div className="detail-row">
                <MdAccessTime size={18} />
                <div>
                  <div className="detail-label">Duration</div>
                  <div className="detail-value">{selectedAppointment.duration} minutes</div>
                </div>
              </div>
            )}

            {selectedAppointment.DOCTOR_NAME && (
              <div className="detail-row">
                <MdPerson size={18} />
                <div>
                  <div className="detail-label">Doctor</div>
                  <div className="detail-value">{selectedAppointment.DOCTOR_NAME}</div>
                </div>
              </div>
            )}

            {selectedAppointment.patientName && (
              <div className="detail-row">
                <MdPerson size={18} />
                <div>
                  <div className="detail-label">Patient</div>
                  <div className="detail-value">{selectedAppointment.patientName}</div>
                </div>
              </div>
            )}

            {(selectedAppointment.PATIENT_MOBILE || selectedAppointment.PATIENT_PHONE) && (
              <div className="detail-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <MdPhone size={18} />
                  <div>
                    <div className="detail-label">Phone</div>
                    <div className="detail-value">{selectedAppointment.PATIENT_MOBILE || selectedAppointment.PATIENT_PHONE}</div>
                  </div>
                </div>
                <div
                  className="whatsapp-btn"
                  style={{
                    backgroundColor: '#25D36620',
                    color: '#25D366',
                    padding: '8px',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.2s',
                    border: '1px solid #25D36620'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    const phone = selectedAppointment.PATIENT_MOBILE || selectedAppointment.PATIENT_PHONE;
                    const cleanNumber = String(phone).replace(/\D/g, '');
                    window.open(`https://wa.me/${cleanNumber}`, '_blank');
                  }}
                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#25D36640'}
                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#25D36620'}
                  title="Open in WhatsApp"
                >
                  <FaWhatsapp size={20} />
                </div>
              </div>
            )}

            <div className="form-actions">
              <button className="btn btn-secondary" onClick={() => setSelectedAppointment(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;